import React, { useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';
import { conversationsAtom, frameParamsAtom, pinAtom, pinMinimizeAtom } from 'src/app/state/atoms';
import LatexHTMLRenderer from '../LatexHTMLRenderer/LatexHTMLRenderer';
import styles from './PinList.module.scss';
import PinButton from '../PinButton/PinButton';
import unpin_all from 'src/assets/images/unpin_all.png';
import minimize from 'src/assets/images/minimize.svg';
import captureEvent from 'src/app/lib/gtm-functions';
import { PinType } from 'src/types/generic';
import { t } from 'i18next';

type GroupedP = {
  [x: string]: Array<PinType>;
};

export function groupPinsById(pins: Array<PinType>): GroupedP {
  const _groupedPins = Array.from(pins).reduce((pinGroup: any, pin) => {
    const { group } = pin;
    if (group) {
      pinGroup[group] = pinGroup[group] ?? [];
      pinGroup[group].push(pin);
    }
    return pinGroup;
  }, {});

  return _groupedPins;
}

function PinList() {
  const [groupedPins, setGroupedPins] = useState<GroupedP>();
  const [pinTitles, setPinTitles] = useState<string[]>();
  const [pinState, setPins] = useRecoilState(pinAtom);
  const [isMinimized, setMinimized] = useRecoilState(pinMinimizeAtom);
  const [conversations] = useRecoilState(conversationsAtom);
  const [frameParams] = useRecoilState(frameParamsAtom);

  useEffect(() => {
    if (pinState.pins.length > 0) {
      setGroupedPins(groupPinsById(pinState.pins));
    }
  }, [pinState]);

  useEffect(() => {
    if (groupedPins) {
      const filteredMessages = conversations.data.messages.filter((message) =>
        Object.keys(groupedPins).includes(String(message.id))
      );

      setPinTitles(filteredMessages.map((message) => message.content));
    }
  }, [conversations.data.messages, groupedPins]);

  const handlePinClick = () => {
    setPins({ pins: [] });
    // Telemetry: Click multipin Icon
    captureEvent({
      event_action: 'Click Multipin Icon',
      event_label: `${pinState.pins.length}`,
      location_in_app: 'pinHeader',
      conversation_id: conversations.data.id,
      mlm_book_code: frameParams?.book_code ?? 'N/A',
      app_id: 'MLMAITutor',
      exercise_id: frameParams?.exercise_id,
      user_role: frameParams?.['user-role'],
      person_id_type: frameParams?.person_id_type,
      login_session_id: frameParams?.login_session_id,
    });
    return;
  };

  // Return the rendered pin header
  const renderPinHeader = (pinTitles: string[], index: number) => {
    return (
      <span className={styles.latexHtmlRenderer}>
        <LatexHTMLRenderer content={pinTitles[index] ?? ''} />
      </span>
    );
  };

  const minimizePins = () => {
    setMinimized(true);
  };

  return (
    <div className={styles.pinsContainer}>
      <div className={`${styles.pinHeader}`}>
        <div className='grow'>
          <p className='text-white font-semibold'>{t('PINS')}</p>
        </div>
        <button className='group' onClick={handlePinClick} aria-label={t('UNPIN_ALL')} type='button'>
          <img src={unpin_all} alt='unpin all' className='inline-block mr-space-2' aria-hidden={true} />
          <span
            role='tooltip'
            aria-hidden={true}
            className={
              'absolute top-12 right-5 scale-0 rounded bg-gray-800 p-space-2 text-xs text-white group-hover:scale-100 z-50'
            }
          >
            {t('UNPIN_ALL')}
          </span>
        </button>
        <button className='group' onClick={minimizePins} aria-label={t('MINIMIZE')}>
          <img src={minimize} alt='minimize' className='inline-block mr-space-2' />
          <span
            role='tooltip'
            className={
              'absolute top-12 right-1 scale-0 rounded bg-gray-800 p-space-2 text-xs text-white group-hover:scale-100 z-50'
            }
            aria-hidden={true}
          >
            {t('MINIMIZE')}
          </span>
        </button>
      </div>
      <div className='px-space-4 pt-space-4'>
        {!isMinimized &&
          groupedPins &&
          pinTitles &&
          Object.entries(groupedPins).map(([groupId, pins], index) => (
            <div key={groupId} className={`${styles.pinGroup}`}>
              {pins[0]?.pinId !== 'pin-header' ? (
                renderPinHeader(pinTitles, index)
              ) : (
                <span className='font-bold'>{t('INTRODUCTION')}</span>
              )}

              {/* Render each pin within the group */}
              {pins.length > 0 &&
                pins.map((pin) => (
                  <div key={pin.pinId} className={`${styles.pinWrapper}`}>
                    <div className='grow me-1'>
                      <LatexHTMLRenderer content={pin.content ?? ''} />
                    </div>
                    <div>
                      <PinButton label={pin.content} providedPinId={pin.pinId} group={pin.group} />
                    </div>
                  </div>
                ))}
            </div>
          ))}
      </div>
    </div>
  );
}

export default PinList;
